/**
 * 📄 Document Details Page
 * Página de detalhes de documento que se adapta ao tipo
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDatabaseDocument } from '../hooks/useDatabaseDocuments';
import { PropertyDetailsContent } from '../components/content/PropertyDetailsContent';
import { ProductDetailsContent } from '../components/content/ProductDetailsContent';
import { DocumentDetailsContent } from '../components/content/DocumentDetailsContent';
import { DeleteDocumentDialog } from '../components/DeleteDocumentDialog';
import { ExportPdfDialog } from '../components/ExportPdfDialog';
import { Icon } from '@/shared/icons'
export const DocumentDetailsPage: React.FC = () => {
  const { id: databaseId, documentId } = useParams<{ id: string; documentId: string }>();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Fetch document based on type
  // Rota backend: GET /api/databases/documents/:id
  const {
    data: document,
    isLoading,
    error
  } = useDatabaseDocument(documentId || '', !!documentId);

  const handleBack = () => {
    navigate(`/databases/${databaseId}`);
  };

  const handleEdit = () => {
    navigate(`/databases/${databaseId}/documents/${documentId}/edit`);
  };

  const handleDeleteSuccess = () => {
    navigate(`/databases/${databaseId}`);
  };

  const getDocumentName = (): string => {
    if (!document) return '';
    const data = document.data as Record<string, unknown>;
    return (data.name as string) || (data.title as string) || 'este documento';
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <div className="flex flex-col items-center gap-4">
          <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
          <span style={{ color: 'var(--theme-muted-text)' }}>Carregando documento...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !document) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <div className="text-center max-w-md">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--theme-error-background)' }}
          >
            <Icon name="alert-circle" className="w-8 h-8" style={{ color: 'var(--theme-error-color)' }} />
          </div>
          <h2
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Documento não encontrado
          </h2>
          <p
            className="mb-6"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            {error?.message || 'O documento solicitado não existe ou foi removido.'}
          </p>
          <button
            onClick={handleBack}
            className="flex items-center gap-2 px-4 py-2 rounded-lg mx-auto"
            style={{
              backgroundColor: 'var(--theme-primary)',
              color: '#ffffff'
            }}
          >
            <Icon name="arrow-left" className="w-4 h-4" />
            Voltar para a base
          </button>
        </div>
      </div>
    );
  }

  // Render based on document type
  switch (document.type) {
    case 'properties':
      return (
        <>
          <PropertyDetailsContent
            document={document as any}
            databaseId={databaseId!}
            onEdit={handleEdit}
            onDelete={() => setShowDeleteDialog(true)}
            onExportPdf={() => setShowExportDialog(true)}
          />
          <DeleteDocumentDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            documentId={document.id}
            documentName={getDocumentName()}
            onSuccess={handleDeleteSuccess}
          />
          <ExportPdfDialog
            isOpen={showExportDialog}
            onClose={() => setShowExportDialog(false)}
            documentId={document.id}
            reference={(document.data as { reference?: string }).reference}
          />
        </>
      );

    case 'vehicles':
      // TODO: Implement VehicleDetailsContent
      return (
        <PlaceholderContent
          type="Veículo"
          document={document}
          onBack={handleBack}
        />
      );

    case 'products':
      return (
        <>
          <ProductDetailsContent
            document={document as any}
            databaseId={databaseId!}
            onEdit={handleEdit}
            onDelete={() => setShowDeleteDialog(true)}
          />
          <DeleteDocumentDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            documentId={document.id}
            documentName={getDocumentName()}
            onSuccess={handleDeleteSuccess}
          />
        </>
      );

    case 'services':
      // TODO: Implement ServiceDetailsContent
      return (
        <PlaceholderContent
          type="Serviço"
          document={document}
          onBack={handleBack}
        />
      );

    case 'documents':
      return (
        <>
          <DocumentDetailsContent
            document={document as any}
            databaseId={databaseId!}
            onEdit={handleEdit}
            onDelete={() => setShowDeleteDialog(true)}
          />
          <DeleteDocumentDialog
            isOpen={showDeleteDialog}
            onClose={() => setShowDeleteDialog(false)}
            documentId={document.id}
            documentName={getDocumentName()}
            onSuccess={handleDeleteSuccess}
          />
        </>
      );

    default:
      return (
        <PlaceholderContent
          type="Desconhecido"
          document={document}
          onBack={handleBack}
        />
      );
  }
};

/**
 * Placeholder para tipos não implementados
 */
const PlaceholderContent: React.FC<{
  type: string;
  document: any;
  onBack: () => void;
}> = ({ type, document, onBack }) => {
  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
    >
      {/* Header */}
      <div
        className="px-6 py-4 flex items-center gap-4 border-b"
        style={{
          backgroundColor: 'var(--theme-header-background)',
          borderColor: 'var(--theme-header-border)'
        }}
      >
        <button
          onClick={onBack}
          className="p-2 rounded-lg transition-colors"
          style={{ color: 'var(--theme-header-text)' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <Icon name="arrow-left" className="w-5 h-5" />
        </button>
        <h1
          className="text-xl font-bold"
          style={{ color: 'var(--theme-header-text)' }}
        >
          Detalhes do {type}
        </h1>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: 'var(--theme-warning-background)' }}
          >
            <Icon name="alert-circle" className="w-8 h-8" style={{ color: 'var(--theme-warning-color)' }} />
          </div>
          <h2
            className="text-xl font-bold mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Visualização em desenvolvimento
          </h2>
          <p
            className="mb-6"
            style={{ color: 'var(--theme-muted-text)' }}
          >
            A página de detalhes para o tipo "{type}" ainda está sendo desenvolvida.
          </p>

          {/* Raw data preview */}
          <div
            className="text-left p-4 rounded-lg overflow-auto max-h-96"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              border: '1px solid var(--theme-card-border)'
            }}
          >
            <pre className="text-xs" style={{ color: 'var(--theme-card-text)' }}>
              {JSON.stringify(document.data, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};
